
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { type Brand, type SpecialDay } from '@/utils/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { checkForPersonalSpecialDay } from '@/services/notificationService';
import { formatDate } from '@/utils/dateUtils';

interface FavoriteDay {
  id: string;
  name: string;
  date: string;
}

const BRANDS: Brand[] = [
  { id: 'mcdonalds', name: 'McDonald\'s', logo: '/images/mcdonalds.png', silo: 'restaurants' },
  { id: 'burgerking', name: 'Burger King', logo: '/images/burgerking.png', silo: 'restaurants' },
  { id: 'wendys', name: 'Wendy\'s', logo: '/images/wendys.png', silo: 'restaurants' },
  { id: 'chickfila', name: 'Chick-fil-A', logo: '/images/chickfila.png', silo: 'restaurants' },
  
  { id: 'dunkin', name: 'Dunkin\' Donuts', logo: '/images/dunkin.png', silo: 'beverages' },
  { id: 'starbucks', name: 'Starbucks', logo: '/images/starbucks.png', silo: 'beverages' },
  { id: 'krispy', name: 'Krispy Kreme', logo: '/images/krispy.png', silo: 'beverages' },
  { id: 'timhortons', name: 'Tim Horton\'s', logo: '/images/timhortons.png', silo: 'beverages' },
  
  { id: 'cvs', name: 'CVS', logo: '/images/cvs.png', silo: 'convenience' },
  { id: 'riteaid', name: 'Rite Aid', logo: '/images/riteaid.png', silo: 'convenience' },
  { id: 'walgreens', name: 'Walgreens', logo: '/images/walgreens.png', silo: 'convenience' },
  { id: 'walmart', name: 'Walmart', logo: '/images/walmart.png', silo: 'convenience' },
  
  { id: 'showcase', name: 'Showcase Cinemas', logo: '/images/showcase.png', silo: 'entertainment' },
  { id: 'amc', name: 'AMC Theatres', logo: '/images/amc.png', silo: 'entertainment' },
  { id: 'xbox', name: 'Xbox', logo: '/images/xbox.png', silo: 'entertainment' },
  { id: 'playstation', name: 'PlayStation', logo: '/images/playstation.png', silo: 'entertainment' },
];

const BRANDS_BY_SILO = BRANDS.reduce((acc, brand) => {
  if (!acc[brand.silo]) {
    acc[brand.silo] = [];
  }
  acc[brand.silo].push(brand);
  return acc;
}, {} as Record<string, Brand[]>);

const SILO_DISPLAY_NAMES: Record<string, string> = {
  'restaurants': 'Restaurant Silo',
  'beverages': 'Beverages Silo',
  'convenience': 'Convenience Silo',
  'entertainment': 'Entertainment Silo'
};

const DEFAULT_SPECIAL_DAYS: FavoriteDay[] = [
  { id: 'birthday', name: 'Birthday', date: '' },
  { id: 'anniversary', name: 'Anniversary', date: '' },
];

const GiftPreferences: React.FC = () => {
  const [selectedBrands, setSelectedBrands] = useState<string[]>(['chickfila', 'dunkin', 'walmart', 'amc']);
  const [specialDays, setSpecialDays] = useState<SpecialDay[]>(
    DEFAULT_SPECIAL_DAYS.map(day => ({ ...day, enabled: true }))
  );
  const [newDayName, setNewDayName] = useState('');
  const [newDayDate, setNewDayDate] = useState('');
  const [notificationPreferences, setNotificationPreferences] = useState({
    inApp: true,
    sms: true,
    email: false,
  });
  
  useEffect(() => {
    // Load preferences from localStorage if available
    const savedPrefs = localStorage.getItem('giftPreferences');
    if (savedPrefs) {
      try {
        const prefs = JSON.parse(savedPrefs);
        if (prefs.favoriteBrands) setSelectedBrands(prefs.favoriteBrands);
        if (prefs.specialDays) {
          setSpecialDays(prefs.specialDays.map((day: any) => ({
            ...day,
            enabled: day.enabled !== undefined ? day.enabled : true
          })));
        }
        if (prefs.notificationPreferences) {
          setNotificationPreferences(prefs.notificationPreferences);
        }
      } catch (e) {
        console.error('Error parsing saved preferences', e);
      }
    }
    
    // Check if today is a special day
    const specialDaysWithDefaults = specialDays.map(day => ({
      ...day,
      enabled: day.enabled !== undefined ? day.enabled : true
    }));
    checkForPersonalSpecialDay(specialDaysWithDefaults);
  }, []);
  
  const handleBrandToggle = (brandId: string, silo: string) => {
    setSelectedBrands(current => {
      const updated = [...current];
      
      const selectedBrandsInSilo = BRANDS
        .filter(brand => brand.silo === silo && current.includes(brand.id))
        .map(brand => brand.id);
      
      if (current.includes(brandId)) {
        return current.filter(id => id !== brandId);
      } 
      
      const withoutSilo = current.filter(id => {
        const brand = BRANDS.find(b => b.id === id);
        return brand?.silo !== silo;
      });
      
      return [...withoutSilo, brandId];
    });
  };
  
  const handleDateChange = (dayId: string, date: string) => {
    setSpecialDays(current => 
      current.map(day => 
        day.id === dayId ? { ...day, date } : day
      )
    );
  };
  
  const toggleSpecialDayEnabled = (dayId: string, enabled: boolean) => {
    setSpecialDays(current => 
      current.map(day => 
        day.id === dayId ? { ...day, enabled } : day
      )
    );
  };
  
  const addSpecialDay = () => {
    if (!newDayName.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter a name for your special day",
        variant: "destructive"
      });
      return;
    }
    
    const newDay: SpecialDay = {
      id: `custom-${Date.now()}`,
      name: newDayName,
      date: newDayDate,
      enabled: true
    };
    
    setSpecialDays(current => [...current, newDay]);
    setNewDayName('');
    setNewDayDate('');
  };
  
  const removeSpecialDay = (dayId: string) => {
    setSpecialDays(current => current.filter(day => day.id !== dayId));
  };
  
  const savePreferences = () => {
    localStorage.setItem('giftPreferences', JSON.stringify({
      favoriteBrands: selectedBrands,
      specialDays: specialDays,
      notificationPreferences: notificationPreferences
    }));
    
    toast({
      title: "Preferences Saved",
      description: "Your gift preferences have been updated"
    });
    
    // Check if any of today's special days
    checkForPersonalSpecialDay(specialDays);
  };
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Favorite Brands</CardTitle>
          <CardDescription>
            Select 1 brand in each product silo that you'd like to receive as quick gifts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {selectedBrands.map(brandId => {
                const brand = BRANDS.find(b => b.id === brandId);
                if (!brand) return null;
                
                return (
                  <Badge key={brand.id} className="py-1 px-2 bg-givgo-light text-givgo-purple flex items-center gap-1">
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={brand.logo} alt={brand.name} />
                      <AvatarFallback className="text-xs">{brand.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    {brand.name}
                  </Badge>
                );
              })}
              {selectedBrands.length === 0 && (
                <div className="text-sm text-muted-foreground">No favorite brands selected</div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(BRANDS_BY_SILO).map(([silo, brands]) => (
                <div key={silo} className="space-y-2">
                  <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">
                    {SILO_DISPLAY_NAMES[silo] || silo}
                  </h4>
                  <div className="space-y-2">
                    {brands.map(brand => (
                      <div key={brand.id} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`brand-${brand.id}`} 
                          checked={selectedBrands.includes(brand.id)}
                          onCheckedChange={() => handleBrandToggle(brand.id, brand.silo)}
                        />
                        <Label 
                          htmlFor={`brand-${brand.id}`}
                          className="flex items-center cursor-pointer"
                        >
                          <Avatar className="h-5 w-5 mr-2">
                            <AvatarImage src={brand.logo} alt={brand.name} />
                            <AvatarFallback className="text-xs">{brand.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          {brand.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Special Days</CardTitle>
          <CardDescription>
            Add important days when your friends might want to send you gifts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {specialDays.map(day => (
              <div key={day.id} className="flex items-center space-x-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id={`enable-${day.id}`}
                      checked={day.enabled !== false}
                      onCheckedChange={(checked) => toggleSpecialDayEnabled(day.id, !!checked)}
                    />
                    <Label htmlFor={`enable-${day.id}`}>{day.name}</Label>
                  </div>
                  <div className="flex items-center mt-1 ml-6">
                    <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                    <Input
                      id={`day-${day.id}`}
                      type="date"
                      value={day.date || ''}
                      onChange={(e) => handleDateChange(day.id, e.target.value)}
                      disabled={day.enabled === false}
                      className={day.enabled === false ? "opacity-50" : ""}
                    />
                    {day.date && (
                      <span className="ml-2 text-sm text-muted-foreground">
                        {formatDate(day.date)}
                      </span>
                    )}
                  </div>
                </div>
                {!['birthday', 'anniversary'].includes(day.id) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSpecialDay(day.id)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-2">Add New Special Day</h4>
              <div className="flex flex-col space-y-2">
                <div>
                  <Label htmlFor="new-day-name">Day Name</Label>
                  <Input
                    id="new-day-name"
                    placeholder="e.g., Graduation"
                    value={newDayName}
                    onChange={(e) => setNewDayName(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="new-day-date">Date</Label>
                  <Input
                    id="new-day-date"
                    type="date"
                    value={newDayDate}
                    onChange={(e) => setNewDayDate(e.target.value)}
                  />
                </div>
                <Button 
                  variant="outline" 
                  onClick={addSpecialDay}
                >
                  Add Special Day
                </Button>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-2">Notification Settings</h4>
              <p className="text-sm text-muted-foreground mb-4">
                How would you like your network to be notified about your special days?
              </p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="notify-app">In-app notifications</Label>
                  <Checkbox 
                    id="notify-app"
                    checked={notificationPreferences.inApp}
                    onCheckedChange={(checked) => 
                      setNotificationPreferences(prev => ({
                        ...prev,
                        inApp: !!checked
                      }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="notify-sms">SMS notifications</Label>
                  <Checkbox 
                    id="notify-sms"
                    checked={notificationPreferences.sms}
                    onCheckedChange={(checked) => 
                      setNotificationPreferences(prev => ({
                        ...prev,
                        sms: !!checked
                      }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="notify-email">Email notifications</Label>
                  <Checkbox 
                    id="notify-email"
                    checked={notificationPreferences.email}
                    onCheckedChange={(checked) => 
                      setNotificationPreferences(prev => ({
                        ...prev,
                        email: !!checked
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button 
            className="w-full bg-givgo-purple hover:bg-givgo-purple/90"
            onClick={savePreferences}
          >
            Save Preferences
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default GiftPreferences;
